import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { cn } from '../../lib/utils.ts';
import { Command, CommandDialog } from './command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx';

type CommandFilter = (value: string, search: string, keywords?: string[]) => number;

type ResponsiveCommandContextValue = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isDesktop: boolean;
    value?: string;
    shouldFilter?: boolean;
    filter?: CommandFilter;
};

const ResponsiveCommandContext = createContext<ResponsiveCommandContextValue | null>(null);

function useResponsiveCommand(): ResponsiveCommandContextValue {
    const ctx = useContext(ResponsiveCommandContext);
    if (!ctx) {
        throw new Error('ResponsiveCommand.* must be used within <ResponsiveCommand>');
    }
    return ctx;
}

type RootProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Forwarded to the underlying cmdk `Command` (controlled selected value). */
    value?: string;
    shouldFilter?: boolean;
    filter?: CommandFilter;
    children: ReactNode;
};

/**
 * Renders a cmdk command menu as a popover on desktop and a full-screen
 * {@link CommandDialog} on mobile. The dialog variant supplies the close
 * affordance (the `CommandInput` cancel button), which the popover doesn't need
 * since it dismisses on outside-click.
 */
function ResponsiveCommand({
    open,
    onOpenChange,
    value,
    shouldFilter,
    filter,
    children,
}: RootProps): ReactNode {
    const isDesktop = useBreakpoint('sm');

    return (
        <ResponsiveCommandContext.Provider
            value={{ open, onOpenChange, isDesktop, value, shouldFilter, filter }}
        >
            {isDesktop ? (
                <Popover open={open} onOpenChange={onOpenChange}>
                    {children}
                </Popover>
            ) : (
                children
            )}
        </ResponsiveCommandContext.Provider>
    );
}

type TriggerProps = {
    children: ReactNode;
    asChild?: boolean;
};

function Trigger({ children, asChild = true }: TriggerProps): ReactNode {
    const { isDesktop, open, onOpenChange } = useResponsiveCommand();

    if (isDesktop) {
        return <PopoverTrigger asChild={asChild}>{children}</PopoverTrigger>;
    }

    return (
        <Slot
            onClick={() => {
                onOpenChange(!open);
            }}
        >
            {children}
        </Slot>
    );
}

type ContentProps = Omit<ComponentPropsWithoutRef<typeof PopoverContent>, 'children'> & {
    children: ReactNode;
};

function Content({ children, className, ...popoverContentProps }: ContentProps): ReactNode {
    const { isDesktop, open, onOpenChange, value, shouldFilter, filter } = useResponsiveCommand();

    if (isDesktop) {
        return (
            <PopoverContent className={cn('p-0', className)} {...popoverContentProps}>
                <Command value={value} shouldFilter={shouldFilter} filter={filter}>
                    {children}
                </Command>
            </PopoverContent>
        );
    }

    return (
        <CommandDialog
            open={open}
            onOpenChange={onOpenChange}
            value={value}
            shouldFilter={shouldFilter}
            filter={filter}
        >
            {children}
        </CommandDialog>
    );
}

ResponsiveCommand.Trigger = Trigger;
ResponsiveCommand.Content = Content;

export { ResponsiveCommand };
